import React, { FC, useMemo } from 'react';
import { PDRTItem } from './pdrtReportItems';
import { ICertificate } from '../custom-general/custom-certificates/form';

import withPDF from '../../hoc/withPDF';
import useCoverSheetOverview from '../../../hooks/useCoverSheetOverview';

import CustomInfoSheet from '../custom-general/custom-info-sheet';
import CustomCoverPage from '../custom-general/custom-cover-page';
import Conditional from '../../common/conditional';
import CustomSection from '../custom-general/custom-section';
import CustomGeneralPhotos from '../custom-general/custom-general-photos';
import CustomCertificatePdf from '../custom-general/custom-certificates/pdf';
import PDRTDataPage from './pdrt-data-page';
import IsometricDrawing from '../custom-utm/isometric-drawing';
import PDRTDataSummary from './pdrt-data-summary';
import OverviewSheet from '../custom-general/overview-sheet';

import * as models from '../../../utils/models';
import * as utils from '../../../utils/utilities';

interface PDRTReportProps {
	certificates?: ICertificate;
	reportTitle: string;
	executiveSummary: string;
	projectDetails: models.Project;
	pdrtItems: PDRTItem[];
	coverData: models.CustomFVIData;
	drawing: models.PDFImageProps[];
	summaryItems: models.CustomDRTItem[];
	photoRecord: models.PDFImageProps[];
}

const PDRTReport: FC<PDRTReportProps> = (props) => {
	const { inspectionTypeName } = props.projectDetails;

	const referenceDocuments: models.CoverDataItem[] = [
		{
			label: '1. Scope Of Work',
			value: props.coverData?.scopeOfWork,
		},
		{
			label: '2. VT Procedure.',
			value: props.coverData?.vtProcedure,
		},
		{
			label: '3. Rope Access Code of Practice.',
			value: props.coverData?.ropeAccessCodeOfPractice,
		},
		{
			label: '4. Work Procedure.',
			value: props.coverData?.exxonMobilWorkProcedure,
		},
		{
			label: '5. Arridex Procedure.',
			value: props.coverData?.russelsmithProcedure!,
		},
	];

	const standardCriteriaItems: models.CoverDataItem[] = useMemo(() => {
		return props.coverData.coverData.map((item, index) => ({
			label: `${index + 1}. ${item.id}`,
			value: item.name.toString(),
		}));
	}, [props.coverData.coverData]);

	const overview = useCoverSheetOverview();

	return (
		<React.Fragment>
			<CustomCoverPage reportTitle={utils.getExtractedInspectionName(inspectionTypeName)} />

			<CustomInfoSheet
				projectDetails={props.projectDetails}
				workOrderNumber={props.coverData.workOrderNumber}
				reportTitle={props.reportTitle}
			/>

			<OverviewSheet
				overviewData={overview}
				referenceDocuments={referenceDocuments}
				standardCriteriaItems={standardCriteriaItems}
				projectDetails={props.projectDetails}
			/>

			{props.certificates && <CustomCertificatePdf certificates={props.certificates} />}

			<PDRTDataSummary 
				summary={props.executiveSummary} 
				items={props.pdrtItems} 
			/>

			<IsometricDrawing 
				isoImages={props.drawing} 
				label="Drawing / Iso"  
			/>

			{props.pdrtItems.map((reportItem) => (
				<CustomSection key={reportItem.id} sectionTitle="Digital Radiography Data">
					<PDRTDataPage {...reportItem} />
				</CustomSection>
			))}

			<Conditional isVisible={props.photoRecord.length > 0}>
				<CustomGeneralPhotos label="Photographic Record" photos={props.photoRecord} />
			</Conditional>
		</React.Fragment>
	);
};

export default withPDF(PDRTReport);
